// Keeps the spell-template preferences in step with Loremaster's Compass:
// pull on connect (Compass's copy wins when newer or when the layout has
// none), push on every local change after a short debounce. Mounted once
// in App. Reports what it did to the activity log, once per direction.
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useStore } from "../state/store";
import { applyPrefs, prefStamp, prefValues, prefsLaneReady, pullPrefs, pushPrefs, samePrefs, subscribePrefsLane, type PrefValues } from "../state/prefsSync";

export function PrefsSync() {
  const { layout, update, logActivity } = useStore();
  const ready = useSyncExternalStore(subscribePrefsLane, prefsLaneReady, prefsLaneReady);
  const values = prefValues(layout);
  const key = JSON.stringify(values);
  const stamp = prefStamp(layout);
  const pulled = useRef(false);
  const lastPushed = useRef<{ key: string; at: number } | null>(null);
  const applying = useRef(false);
  const [retry, setRetry] = useState(0);   // Compass may start after the DM Screen: look again every minute while it is away

  // on connect: Compass's copy, when it is newer or the layout has none
  useEffect(() => {
    if (!ready || pulled.current) return;
    pulled.current = true;
    void (async () => {
      const r = await pullPrefs();
      if (r === "offline" || r === "error") { pulled.current = false; window.setTimeout(() => setRetry((n) => n + 1), r === "offline" ? 60000 : 15000); return; }   // no Compass yet, or a hiccup: the local cache serves; look again
      if (r === "empty") { if (Object.keys(values).length) { lastPushed.current = { key, at: stamp || Date.now() }; await pushPrefs(values, lastPushed.current.at); logActivity({ text: "Spell-template preferences saved to Compass", meta: "Compass had none yet", tone: "muted" }); } return; }
      if (r.at > stamp && !samePrefs(r.values, values)) {
        applying.current = true;
        update((l) => applyPrefs(l, r.values, r.at));
        lastPushed.current = { key: JSON.stringify(r.values), at: r.at };
        logActivity({ text: "Spell-template preferences loaded from Compass", meta: `saved ${new Date(r.at).toLocaleString()}`, tone: "muted" });
      } else if (r.at < stamp && !samePrefs(r.values, values)) {
        lastPushed.current = { key, at: stamp };
        await pushPrefs(values, stamp);
        logActivity({ text: "Spell-template preferences saved to Compass", meta: "this browser's were newer", tone: "muted" });
      } else lastPushed.current = { key, at: r.at };
    })();
  }, [ready, retry]);

  // on change: push, debounced, stamped now
  useEffect(() => {
    if (!ready || !pulled.current) return;
    if (applying.current) { applying.current = false; return; }
    if (lastPushed.current && lastPushed.current.key === key) return;
    const at = Date.now();
    const timer = window.setTimeout(async () => {
      const r = await pushPrefs(values as PrefValues, at);
      if (r.ok) { lastPushed.current = { key, at }; update((l) => (prefStamp(l) === at ? l : { ...l, effectPrefsAt: at })); }
    }, 1500);
    return () => window.clearTimeout(timer);
  }, [ready, key]);
  return null;
}
